import { DialogApplication } from './application';
import { Config, IACCommunication, IACHub, Navigator } from '.';
import { IACUsecase } from './iacType/iacUsecase';
import { KeepAlive, Theme, ApplicationLifeCycle, PerformanceTracking } from './interface';
import { NavigatorError } from './navigator-services';
import * as Logger from '@navigator/frontend-logger';
import { IAC_EVENT_TYPES } from './constants';

export class IACEndpoint {

  public static get SYSNAME(): string {
    return 'navigator';
  }

  public static dispatch(usecase: IACUsecase): void {
    if (!(usecase instanceof IACUsecase)) {
      throw new NavigatorError('usecase must be type of IACUsecase');
    }

    if (usecase.getName() in this) {
      (this as any)[usecase.getName()](usecase);
    }
  }

  /* IAC USECASE HANDLERS FOR INTERNAL PURPOSES */

  private static __connect__(usecase: IACUsecase) {
    const senderApp = Navigator.getApplication(usecase.getSender());

    senderApp.setIacConnected(true);

    IACCommunication.sendMessage(
      IACCommunication.createUsecase('__connected__'),
      senderApp,
    );

    IACCommunication.sendQueuedMessages(senderApp);
  }

  private static __disconnect__(usecase: IACUsecase) {
    const senderApp = Navigator.getApplication(usecase.getSender());

    IACCommunication.sendMessage(
      IACCommunication.createUsecase('__disconnected__'),
      senderApp,
    );

    senderApp.setIacConnected(false);
  }

  /* istanbul ignore next function */
  private static logout() {
    // FIXME: deprecated [POS-1494]
    // The use case is deprecated, but still used
    Navigator.logout();
  }

  private static logout_navigator(usecase: IACUsecase) {
    const payload = usecase.getPayload()[0] || {};

    Navigator.logout(payload.targeturl || null);
  }

  /* istanbul ignore next function */
  private static reinitialize(usecase: IACUsecase) {
    // FIXME: deprecated [POS-1494]
    // The use case is deprecated and it seems that it is not used anymore
    Navigator.reinitialize.apply(Navigator, usecase.getPayload());
  }

  private static reinitialize_navigator(usecase: IACUsecase) {
    Navigator.reinitialize(usecase.getPayload()[0].goto_feature);
  }

  /* istanbul ignore next function */
  private static reinitialize_app(usecase: IACUsecase) {
    // FIXME: deprecated [POS-1494]
    // The use case is deprecated and it seems that it is not used anymore
    const senderApp = Navigator.getApplication(usecase.getSender());

    KeepAlive.call(() => {
      senderApp.reinitialize();
    });
  }

  private static reinitialize_application(usecase: IACUsecase) {
    const senderApp = Navigator.getApplication(usecase.getSender());

    KeepAlive.call(() => {
      senderApp.reinitialize();
    });
  }

  /* istanbul ignore next function */
  private static open_feature(usecase: IACUsecase) {
    // FIXME: deprecated [POS-1494]
    // The use case is deprecated and it seems that it is not used anymore
    Navigator.handleFeature.apply(Navigator, usecase.getPayload());
  }

  private static open_goto_feature(usecase: IACUsecase) {
    const gotoOptions = usecase.getPayload()[0];

    Navigator.handleFeature(
      gotoOptions.goto_feature,
      gotoOptions.mediacode,
      gotoOptions.data,
    );
  }

  private static close_application(usecase: IACUsecase) {
    const senderApp = Navigator.getApplication(usecase.getSender());

    if (senderApp instanceof DialogApplication) {
      Navigator.closeDialog(senderApp.getSysname());

    } else {
      Logger.warn('Usecase close_application is currently only supported for DialogApplications.');
    }
  }

  private static theme_switched(usecase: IACUsecase) {
    const theme = usecase.getPayload()[0].theme;
    const saved = usecase.getPayload()[0].saved;

    Theme.setActive(theme);

    /* istanbul ignore next else */
    if (saved) {
      Navigator.update();
    }
  }

  private static theme_sync(usecase: IACUsecase) {
    const theme = usecase.getPayload()[0].theme;

    // SAK: move to an interface
    /* istanbul ignore next else */
    if (!document.getElementsByTagName('html')[0].classList.contains(theme)) {
      /*
       * The theme has been synced.
       * We transform the use case ...
       */
      const confirmUsecase = IACCommunication.createUsecase('theme_switched', {
        theme: usecase.getPayload()[0].theme,
        theme_bg: usecase.getPayload()[0].theme_bg,
        saved: true,
      });
      confirmUsecase.asRPE();

      IACHub.handle(confirmUsecase);
    }
  }

  private static hide_navigator() {
    Navigator.hide();
  }

  private static show_navigator() {
    Navigator.show();
  }

  /* istanbul ignore next function */
  private static reload_navigator() {
    window.location.reload();
  }

  private static init_mailvelope_install_firefox() {
    const mvFrame = document.createElement('iframe');

    mvFrame.setAttribute('width', '0');
    mvFrame.setAttribute('height', '0');

    // SAK: viel zu harte abhängigkeit
    mvFrame.setAttribute('src', ' https://addons.mozilla.org/firefox/downloads/latest/mailvelope/');

    document.body.appendChild(mvFrame);

    setTimeout(() => {
      document.body.removeChild(mvFrame);
    }, 1500);
  }

  private static application_ready(usecase: IACUsecase) {
    const senderApp = Navigator.getApplication(usecase.getSender());

    /* istanbul ignore next else */
    if (senderApp.config.start_in_background) {
      // if a dialog was started in the background, the focused application must be informed when it is activated
      // (for a normal dialog, this has already been done by Navigator.openDialog)
      const focusedApp = Navigator.getFocusedApplication();
      /* istanbul ignore next else */
      if (focusedApp) {
        IACCommunication.sendMessage(
          IACCommunication.createEvent(IAC_EVENT_TYPES.ONDISABLED, { count: ++focusedApp.iacDisabledCounter }),
          focusedApp,
        );
      }
    }

    ApplicationLifeCycle.activateDialog(senderApp);
  }

  private static monitoring_start_mark(usecase: IACUsecase) {
    const payload = usecase.getPayload();

    PerformanceTracking.start(payload[0].id);
  }

  private static monitoring_end_mark(usecase: IACUsecase) {
    const payload = usecase.getPayload();

    PerformanceTracking.end(payload[0].ids);
  }

  static monitoring_delete_mark(usecase: IACUsecase): void {
    const payload = usecase.getPayload();

    PerformanceTracking.delete(payload[0].id);
  }

  // tslint:disable-next-line: no-unused-variable
  private static async redirect_navigator(usecase: IACUsecase): Promise<void> {
    const senderApp = Navigator.getApplication(usecase.getSender());
    const payload = usecase.getPayload()[0];

    /* istanbul ignore next else */
    if (senderApp.config.allow_redirect_navigator) {
      const redirectUrl = new URL(Config.get().navigator.external_redirect_url);
      redirectUrl.searchParams.append('target_url', payload.targetUrl);
      redirectUrl.searchParams.append('iac_appname', senderApp.config.sysname);

      return fetch(redirectUrl.toString(), { credentials: 'same-origin' })
        .then(async (res) => {
          const content = await res.json();
          if (content.isAllowedToRedirect) {
            await KeepAlive.call(() => {
              window.location.replace(payload.targetUrl);
            });
          }
        })
        .catch((reason) => {
          Logger.error(`redirect_navigator: Request for ${redirectUrl.toString()} failed`, reason);
        });

    }
  }

}
